import React, { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Sparkles,
  Bell,
  ShieldAlert,
  Info,
  Archive,
  PackageCheck,
  AlertTriangle,
} from "lucide-react";
import { subDays, startOfDay, endOfDay, format, parseISO } from "date-fns";
import API from "../../utils/api";

const STATUS_META = {
  HMI: { label: "Hold Money Issue", color: "#ef4444", soft: "#fee2e2" },
  NEW: { label: "New", color: "#10b981", soft: "#d1fae5" },
  "IN PROGRESS": { label: "In Progress", color: "#f59e0b", soft: "#fde68a" },
  RECEIVED: { label: "Received", color: "#f97316", soft: "#fed7aa" },
  RTD: { label: "Ready to Dispatch", color: "#8b5cf6", soft: "#ddd6fe" },
  PNA: { label: "PNA", color: "#eab308", soft: "#fef08a" },
  SHIPPED: { label: "Shipped", color: "#3b82f6", soft: "#bfdbfe" },
  ARCHIVED: { label: "Archived", color: "#64748b", soft: "#e2e8f0" },
};

const KPI_ORDER = ["NEW", "RTD", "HMI", "PNA", "SHIPPED", "ARCHIVED"];
const FONT = { fontFamily: "'IBM Plex Sans', 'Poppins', sans-serif" };

const SectionHeader = ({ title, subtitle, icon: Icon, right }) => (
  <div className="flex items-start justify-between gap-3 mb-5">
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="p-2 rounded-xl bg-slate-100 text-slate-600 shrink-0">
          <Icon size={18} />
        </div>
      )}
      <div>
        <h3 className="text-[15px] font-semibold text-slate-800 leading-tight">
          {title}
        </h3>
        {subtitle && (
          <p className="text-[12.5px] text-slate-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {right}
  </div>
);

const TrendBadge = ({ value }) => {
  const v = Math.round(value);
  const up = v >= 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11.5px] font-semibold px-1.5 py-0.5 rounded-md ${
        up ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"
      }`}
    >
      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      {up ? "+" : ""}
      {v}%
    </span>
  );
};

const pctChange = (cur, prev) => {
  if (!prev) return cur > 0 ? 100 : 0;
  return ((cur - prev) / prev) * 100;
};

const ExtraCharts = () => {
  const [statusStats, setStatusStats] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, ordersRes] = await Promise.all([
          API.get("/easyshiporders/status-stats"),
          API.get("/easyshiporders/search-by-date", {
            params: {
              page: 1,
              limit: 100000,
              startDate: startOfDay(subDays(new Date(), 29)).toISOString(),
              endDate: endOfDay(new Date()).toISOString(),
            },
          }),
        ]);
        setStatusStats(statsRes.data);
        setOrders(ordersRes.data.orders || []);
      } catch (err) {
        console.error("Failed to fetch extra chart data:", err);
      }
    };
    fetchData();
  }, []);

  const daily = useMemo(() => {
    const map = new Map();
    orders.forEach((o) => {
      const ts = o.createdAt
        ? new Date(o.createdAt)
        : o.date
          ? parseISO(o.date)
          : null;
      if (!ts || isNaN(ts.getTime())) return;
      const key = format(ts, "yyyy-MM-dd");
      const entry = map.get(key) || { date: key, count: 0, byStatus: {} };
      if (!entry.byStatus[o.status]) entry.byStatus[o.status] = 0;
      entry.byStatus[o.status] += 1;
      entry.count += 1;
      map.set(key, entry);
    });
    const arr = Array.from(map.values());
    arr.sort((a, b) => (a.date < b.date ? -1 : 1));
    return arr;
  }, [orders]);

  const dailyChart = useMemo(
    () =>
      daily.map((d) => ({
        name: format(new Date(d.date), "MMM d"),
        Orders: d.count,
      })),
    [daily],
  );

  const weeklyChart = useMemo(() => {
    const weeks = [
      { name: "W-4", Orders: 0 },
      { name: "W-3", Orders: 0 },
      { name: "W-2", Orders: 0 },
      { name: "W-1", Orders: 0 },
      { name: "This Week", Orders: 0 },
    ];
    const now = Date.now();
    daily.forEach((d) => {
      const ageDays = (now - new Date(d.date).getTime()) / 86400000;
      const dayIdx = Math.floor(ageDays / 7);
      const idx = Math.min(4, Math.max(0, dayIdx));
      weeks[idx].Orders += d.count;
    });
    return weeks;
  }, [daily]);

  const statusDaily = useMemo(() => {
    const out = {};
    KPI_ORDER.forEach((s) => (out[s] = []));
    daily.forEach((day) => {
      const label = format(new Date(day.date), "MMM d");
      KPI_ORDER.forEach((s) => {
        out[s].push({ name: label, count: day.byStatus[s] || 0 });
      });
    });
    return out;
  }, [daily]);

  const totalOrders = useMemo(
    () =>
      statusStats
        ? Object.values(statusStats).reduce((a, b) => a + (b || 0), 0)
        : 0,
    [statusStats],
  );

  const donutData = useMemo(
    () =>
      statusStats
        ? Object.entries(statusStats)
            .map(([name, value]) => ({
              name,
              label: STATUS_META[name]?.label || name,
              value: value || 0,
              color: STATUS_META[name]?.color || "#94a3b8",
            }))
            .filter((d) => d.value > 0)
            .sort((a, b) => b.value - a.value)
        : [],
    [statusStats],
  );

  const progressData = useMemo(
    () =>
      statusStats
        ? Object.entries(statusStats)
            .map(([name, value]) => ({
              name,
              label: STATUS_META[name]?.label || name,
              value: value || 0,
              color: STATUS_META[name]?.color || "#94a3b8",
            }))
            .sort((a, b) => b.value - a.value)
        : [],
    [statusStats],
  );

  const weeklyPerf = useMemo(
    () =>
      KPI_ORDER.slice(0, 4).map((key) => {
        const meta = STATUS_META[key];
        const total = statusStats?.[key] || 0;
        const series = statusDaily[key];
        const last7 = series.slice(-7);
        const prev7 = series.slice(-14, -7);
        const cur = last7.reduce((a, b) => a + b.count, 0);
        const prev = prev7.reduce((a, b) => a + b.count, 0);
        return {
          key,
          label: meta.label,
          color: meta.color,
          total,
          cur,
          prev,
          change: pctChange(cur, prev),
          series: last7,
        };
      }),
    [statusStats, statusDaily],
  );

  const insights = useMemo(() => {
    const list = [];
    const count = (k) => statusStats?.[k] || 0;
    const thisWeek = daily.slice(-7).reduce((a, b) => a + b.count, 0);
    const lastWeek = daily.slice(-14, -7).reduce((a, b) => a + b.count, 0);
    const trend = pctChange(thisWeek, lastWeek);

    if (count("HMI") > 0) {
      list.push({
        severity: "critical",
        icon: ShieldAlert,
        title: `${count("HMI")} orders on hold`,
        body: "Hold Money Issue (HMI) orders require your immediate review.",
      });
    } else {
      list.push({
        severity: "ok",
        icon: ShieldAlert,
        title: "No hold money issues",
        body: "There are currently no HMI orders needing attention.",
      });
    }

    if (count("NEW") > 0) {
      list.push({
        severity: "info",
        icon: Bell,
        title: `${count("NEW")} new orders awaiting processing`,
        body: "New orders are pending and ready to be processed.",
      });
    } else {
      list.push({
        severity: "ok",
        icon: Bell,
        title: "No pending new orders",
        body: "All new orders have been processed.",
      });
    }

    if (count("PNA") > 0) {
      list.push({
        severity: "warning",
        icon: Info,
        title: `${count("PNA")} PNA orders`,
        body: "Package not attempted orders may need rescheduling or follow-up.",
      });
    }

    list.push({
      severity: trend >= 0 ? "info" : "warning",
      icon: TrendingUp,
      title: `${thisWeek} orders this week`,
      body: `${trend >= 0 ? "+" : ""}${Math.round(trend)}% vs the previous 7 days.`,
    });

    return list.slice(0, 4);
  }, [statusStats, daily]);

  const severityStyles = {
    critical: { border: "#fda4af", bg: "#fff1f2", text: "#be123c" },
    warning: { border: "#fcd34d", bg: "#fffbeb", text: "#b45309" },
    info: { border: "#bfdbfe", bg: "#eff6ff", text: "#1d4ed8" },
    ok: { border: "#a7f3d0", bg: "#ecfdf5", text: "#047857" },
  };

  const chartTooltipStyle = {
    borderRadius: 12,
    border: "1px solid #eef0f4",
    fontSize: 12.5,
    boxShadow: "0 8px 24px rgba(16,24,40,0.08)",
  };

  return (
    <div style={FONT} className="mt-16 grid grid-cols-1 gap-6">
      {/* Donut + Bar volume */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Donut */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
          <SectionHeader
            icon={PackageCheck}
            title="Order Status Breakdown"
            subtitle="Distribution of all orders by status"
            right={
              <span className="text-[12px] font-semibold text-slate-500 bg-slate-100 rounded-lg px-2.5 py-1">
                {totalOrders.toLocaleString()} total
              </span>
            }
          />
          <div className="relative h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={62}
                  outerRadius={92}
                  paddingAngle={3}
                  strokeWidth={2}
                  stroke="#ffffff"
                >
                  {donutData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [
                    `${value.toLocaleString()} orders`,
                    STATUS_META[name]?.label || name,
                  ]}
                  contentStyle={chartTooltipStyle}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <p className="text-[26px] font-bold text-slate-800 leading-none">
                {totalOrders.toLocaleString()}
              </p>
              <p className="text-[11.5px] text-slate-400 mt-1">Total Orders</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2">
            {donutData.map((d) => (
              <div
                key={d.name}
                className="flex items-center justify-between text-[12.5px]"
              >
                <span className="flex items-center gap-2 text-slate-500 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: d.color }}
                  />
                  <span className="truncate">{d.label}</span>
                </span>
                <span className="font-semibold text-slate-700">
                  {d.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Daily bar chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/80 p-6">
          <SectionHeader
            icon={Archive}
            title="Daily Order Volume"
            subtitle="Orders created per day over the past 30 days"
            right={
              <span className="text-[12px] font-semibold text-slate-500 bg-slate-100 rounded-lg px-2.5 py-1">
                {daily.reduce((a, b) => a + b.count, 0).toLocaleString()} orders
              </span>
            }
          />
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dailyChart}
                margin={{ top: 8, right: 8, left: -18, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="dailyFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#6366f1" />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#eef0f4"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11.5, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  interval={3}
                />
                <YAxis
                  tick={{ fontSize: 11.5, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  formatter={(value) => [`${value} orders`, "Volume"]}
                  cursor={{ fill: "rgba(99,102,241,0.06)" }}
                  contentStyle={chartTooltipStyle}
                />
                <Bar
                  dataKey="Orders"
                  fill="url(#dailyFill)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Area trend + weekly volume */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Area trend */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
          <SectionHeader
            icon={TrendingUp}
            title="Order Trends"
            subtitle="Daily order trend over the past 30 days"
          />
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={dailyChart}
                margin={{ top: 8, right: 8, left: -18, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#eef0f4"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11.5, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  interval={3}
                />
                <YAxis
                  tick={{ fontSize: 11.5, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  formatter={(value) => [`${value} orders`, "Orders"]}
                  labelStyle={{ fontWeight: 600, color: "#1f2937", fontSize: 12.5 }}
                  contentStyle={chartTooltipStyle}
                />
                <Area
                  type="monotone"
                  dataKey="Orders"
                  stroke="#8b5cf6"
                  strokeWidth={2.5}
                  fill="url(#trendFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Weekly volume bar */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
          <SectionHeader
            icon={AlertTriangle}
            title="Weekly Order Volume"
            subtitle="Aggregated order volume over the last 5 weeks"
            right={
              <span className="text-[12px] font-semibold text-slate-500 bg-slate-100 rounded-lg px-2.5 py-1">
                {weeklyChart
                  .reduce((a, b) => a + b.Orders, 0)
                  .toLocaleString()}{" "}
                total
              </span>
            }
          />
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={weeklyChart}
                margin={{ top: 8, right: 8, left: -18, bottom: 0 }}
                barSize={46}
              >
                <defs>
                  <linearGradient id="volFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#6366f1" />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#eef0f4"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11.5, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11.5, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  formatter={(value) => [`${value} orders`, "Volume"]}
                  cursor={{ fill: "rgba(99,102,241,0.06)" }}
                  contentStyle={chartTooltipStyle}
                />
                <Bar dataKey="Orders" fill="url(#volFill)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Progress bars + weekly performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
          <SectionHeader
            icon={Archive}
            title="Status Breakdown"
            subtitle="Share of total orders per status"
            right={
              <span className="text-[12px] font-semibold text-slate-500 bg-slate-100 rounded-lg px-2.5 py-1">
                {progressData.length} statuses
              </span>
            }
          />
          <div className="space-y-4">
            {progressData.map((s) => {
              const pct = totalOrders ? (s.value / totalOrders) * 100 : 0;
              return (
                <div key={s.name}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[12.5px] font-medium text-slate-600">
                      {s.label}
                    </span>
                    <span className="text-[12px] font-semibold text-slate-500">
                      {s.value.toLocaleString()}
                      <span className="text-slate-300 font-normal ml-1.5">
                        {pct.toFixed(1)}%
                      </span>
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: s.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
          <SectionHeader
            icon={Sparkles}
            title="Weekly Performance"
            subtitle="This week vs previous week · last 7 days"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {weeklyPerf.map((w) => (
              <div
                key={w.key}
                className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-2 text-[12.5px] font-medium text-slate-500">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: w.color }}
                    />
                    {w.label}
                  </span>
                  <span className="text-[12px] font-semibold text-slate-700">
                    {w.cur.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-end gap-2">
                  <p className="text-[22px] font-bold leading-none text-slate-800">
                    {w.total.toLocaleString()}
                  </p>
                  <TrendBadge value={w.change} />
                </div>
                <div className="mt-2.5 h-9">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={w.series}
                      margin={{ top: 2, right: 0, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id={`wk-${w.key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={w.color} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={w.color} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke={w.color}
                        strokeWidth={1.8}
                        fill={`url(#wk-${w.key})`}
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Top value:{" "}
                  <span className="font-semibold text-slate-500">
                    {w.total.toLocaleString()}
                  </span>{" "}
                  overall
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
        <SectionHeader
          icon={Bell}
          title="Insights & Alerts"
          subtitle="Actionable intelligence computed from live order data"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {insights.map((ins, i) => {
            const st = severityStyles[ins.severity];
            const Icon = ins.icon;
            return (
              <div
                key={i}
                className="rounded-xl p-4"
                style={{
                  backgroundColor: st.bg,
                  border: `1px solid ${st.border}`,
                }}
              >
                <div
                  className="flex items-center gap-2 mb-2"
                  style={{ color: st.text }}
                >
                  <Icon size={16} />
                  <span className="text-[12.5px] font-semibold">
                    {ins.title}
                  </span>
                </div>
                <p className="text-[12.5px] leading-relaxed" style={{ color: st.text }}>
                  {ins.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExtraCharts;